/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  TUseQuantityStock,
  TProductAvailibilityStatus,
  ProductAttributeDto,
  ProductDto,
  TLabelServiceEntry,
  FacetDto
} from "@hsr/types";
import type { TFunction } from "i18next";

export const calculateAvailibility = (
  qty: number,
  productStock: TUseQuantityStock | null
): TProductAvailibilityStatus => {
  if (productStock === null) return "noInformation";

  const { quantity: availableQuantity, secondaryQuantity } = productStock;
  if (availableQuantity === undefined) {
    return "noInformation";
  }
  if (availableQuantity > 0 && availableQuantity >= qty) {
    return "available";
  }
  if (availableQuantity > 0 && availableQuantity < qty) {
    return "availableLimited";
  }
  if (availableQuantity <= 0 && secondaryQuantity > 0) {
    return "availableSoon";
  }
  if (availableQuantity <= 0 && secondaryQuantity <= 0) {
    return "outOfStock";
  }

  return "noInformation";
};

export const getFirstParamValue = (param: string | string[] | undefined) => {
  if (Array.isArray(param)) return param[0];
  return param;
};

export const sortFacetNames = (
  facetsArr: ProductAttributeDto[]
): ProductAttributeDto[] => {
  const facetArrSorted: ProductAttributeDto[] = [];
  const pos1 = facetsArr.findIndex((detail) => detail.id === "IND_DN");
  const pos2 = facetsArr.findIndex((detail) => detail.id === "IND_Size");
  const pos3 = facetsArr.findIndex((detail) => detail.id === "IND_Zoll");

  if (pos1 !== -1) facetArrSorted.push(facetsArr[pos1]);
  if (pos2 !== -1) facetArrSorted.push(facetsArr[pos2]);
  if (pos3 !== -1) facetArrSorted.push(facetsArr[pos3]);

  facetsArr
    .filter((facet) => !["IND_DN", "IND_Size", "IND_Zoll"].includes(facet.id!))
    .forEach((facet) => facetArrSorted.push(facet));

  return facetArrSorted;
};

export const labelFacets = (
  facetsArr: ProductAttributeDto[],
  labelArr: TLabelServiceEntry[],
  lng: string,
  fallbackLng: string
): ProductAttributeDto[] => {
  return facetsArr.map((facet) => {
    const label = labelArr?.find((label) => label.id === facet.id);
    if (label) {
      let labelLng = lng;

      // does localization match? if not, try fallback language
      if (!label.descriptions[labelLng]) {
        if (label.descriptions[fallbackLng]) {
          labelLng = fallbackLng;
        } else {
          return facet;
        }
      }
      return {
        ...facet,
        orderNo: label.orderNo,
        section: label.section,
        label: label.descriptions[labelLng].description
      };
    }

    return facet;
  });
};

// used for labeling facets from product.meta
export const labelMataFacets = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any, any>,
  facetsArr: FacetDto[],
  labelArr: TLabelServiceEntry[] | undefined, // undefined if label service is not used
  lng: string,
  fallbackLng: string,
  useFacetJson: boolean
): ProductAttributeDto[] => {
  return facetsArr.map((facet) => {
    // label service is not used, so we use i18n translation
    if (!useFacetJson) {
      return { ...facet, name: t(`facets:${facet.name}`) };
    }

    // label service is used
    // ->  facet.id might be 'de.IND_Size' instead of 'IND_Size'
    const label = labelArr?.find(
      (label) =>
        label.id === facet.id ||
        (facet.id && label.id === facet.id.split(".").pop())
    );
    if (label) {
      let labelLng = lng;

      // does localization match? if not, try fallback language
      if (!label.descriptions[labelLng]) {
        if (label.descriptions[fallbackLng]) {
          labelLng = fallbackLng;
        } else {
          return facet;
        }
      }
      return {
        ...facet,
        name: label.descriptions[labelLng].description
      };
    }

    return { ...facet, name: t(`facets:${facet.name}`) };
  });
};

// replace / with $ to avoid routing issues
export const sanitizeProductId = (productId: string) => {
  return productId.replace(/\//g, "Sls");
};

// replace Sls with /
export const deSanitizeProductId = (sanitizedProductId: string) => {
  return sanitizedProductId.replace(/Sls/g, "/");
};

export const extractProductId = (
  product: ProductDto,
  returnId: "productId" | "hsrProductNo"
): string => {
  const productId = product.id || "";
  const hsrProductNo = product.hsrProductNo;

  switch (returnId) {
    case "productId":
      return productId;
    case "hsrProductNo":
      if (hsrProductNo) return hsrProductNo;
  }

  return productId;
};
